import {
    PapyrusDocumentFormat,
    PapyrusDocumentRepresentation,
    PapyrusViewerType,
} from "./DocumentModels";

export default class DocumentRepresentationService {
    static getFormat(contentType: string): PapyrusDocumentFormat {
        if (contentType === "application/pdf") {
            return "pdf";
        }

        if (contentType.startsWith("image/")) {
            return "image";
        }

        if (DocumentRepresentationService.isTextDocument(contentType)) {
            return "text-document";
        }

        if (DocumentRepresentationService.isStructuredFhir(contentType)) {
            return "fhir-structured";
        }

        return "unsupported";
    }

    static getViewerType(contentType: string): PapyrusViewerType {
        switch (DocumentRepresentationService.getFormat(contentType)) {
            case "pdf":
                return "pdf-viewer";
            case "image":
                return "image-viewer";
            case "text-document":
                return "text-document-viewer";
            case "fhir-structured":
                return "structured-fhir-viewer";
            default:
                return "unsupported-viewer";
        }
    }

    static getLabelKey(contentType: string): string {
        switch (DocumentRepresentationService.getFormat(contentType)) {
            case "pdf":
                return "document.representation.pdf";
            case "image":
                return "document.representation.image";
            case "text-document":
                return "document.representation.textdocument";
            case "fhir-structured":
                return "document.representation.fhirstructured";
            default:
                return "document.representation.unsupported";
        }
    }

    static buildRepresentations(documentReference: any): PapyrusDocumentRepresentation[] {
        return (documentReference.content ?? [])
            .map((content: any, index: number) => {
                const contentType = content.attachment?.contentType;

                if (!contentType) {
                    return undefined;
                }

                return {
                    id: `${documentReference.id}-${index}`,
                    documentReferenceId: documentReference.id,
                    contentIndex: index,
                    contentType,
                    labelKey: DocumentRepresentationService.getLabelKey(contentType),
                    format: DocumentRepresentationService.getFormat(contentType),
                    viewerType: DocumentRepresentationService.getViewerType(contentType),
                    attachmentUrl: content.attachment?.url,
                    attachmentData: content.attachment?.data,
                } satisfies PapyrusDocumentRepresentation;
            })
            .filter((representation: PapyrusDocumentRepresentation | undefined): representation is PapyrusDocumentRepresentation => !!representation);
    }

    static getViewerRoute(documentReferenceId: string, contentIndex: number): string {
        return `/DocumentViewer/${documentReferenceId}/${contentIndex}`;
    }

    private static isStructuredFhir(contentType: string): boolean {
        return [
            "application/fhir+json",
            "application/fhir+xml",
            "application/json",
        ].includes(contentType);
    }

    private static isTextDocument(contentType: string): boolean {
        return [
            "text/plain",
            "text/html",
            "application/rtf",
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            "application/vnd.oasis.opendocument.text",
        ].includes(contentType);
    }
}
